/**
 * One person's permissions, in one place.
 *
 * What this replaces: a standalone Permissions page holding a role x module x
 * action grid — 34 modules by 6 actions of bare checkboxes — with no way to
 * answer the question an operator actually arrives with, which is "what can
 * THIS person do". Role templates still matter, so they are still editable,
 * but they belong behind a disclosure rather than in front of the question.
 *
 * The model has two independent halves and the dialog keeps them visibly
 * separate:
 *   ROLE          what a person may DO (view / create / edit / ...), shared by
 *                 everyone holding that role.
 *   MODULE ACCESS where they may do it, per person. Unrestricted by default,
 *                 meaning the role decides and keeps deciding; narrowing it
 *                 freezes the account to exactly the modules picked.
 *
 * Effective access is role INTERSECT module access, computed server-side, and
 * shown back so a grant the role silently denies is visible rather than
 * mysterious.
 */
import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, ShieldCheck, X } from 'lucide-react'
import clsx from 'clsx'

import api from '@/services/api'
import { WORKFLOW_META, WORKFLOW_ORDER } from '@/config/workflowModules'
import type { ModuleGroup } from '@/config/workflowModules'
import type { PlatformModuleId } from '@/types'

const GROUP_LABELS: Record<ModuleGroup, string> = {
  dashboards: 'Dashboards',
  'pre-closing': 'Pre-Closing',
  'post-closing': 'Post-Closing',
  tools: 'Tools',
}

const ACTIONS = ['view', 'create', 'edit', 'delete', 'approve', 'export'] as const

export interface PermissionsUser {
  id: string
  name: string
  email: string
  role: string
  is_super_admin?: boolean
  module_access: string[] | null
  visible_modules: string[]
}

type RbacPayload = {
  roles: Record<string, Record<string, string[]>>
  roleLabels: Record<string, string>
  modules: string[]
  actions: string[]
}

function moduleLabel(id: string): string {
  if (id === '*') return 'All modules'
  return WORKFLOW_META[id as PlatformModuleId]?.label ?? id
}

/** The actions a role grants on a module, wildcard included. */
function roleActions(spec: Record<string, string[]> | undefined, moduleId: string): string[] {
  if (!spec) return []
  const direct = spec[moduleId] ?? []
  const wildcard = spec['*'] ?? []
  return [...new Set([...direct, ...wildcard])]
}

export default function UserPermissionsDialog({
  user,
  onClose,
}: {
  user: PermissionsUser
  onClose: () => void
}) {
  const queryClient = useQueryClient()
  const [role, setRole] = useState(user.role)
  /** null = unrestricted. Distinct from an empty array, which would be a
   *  lockout — so the UI never lets an empty selection be saved as a list. */
  const [modules, setModules] = useState<string[] | null>(user.module_access)
  const [error, setError] = useState<string | null>(null)
  const [showRoleMatrix, setShowRoleMatrix] = useState(false)

  const rbac = useQuery({
    queryKey: ['settings', 'rbac'],
    queryFn: async () => (await api.get<RbacPayload>('/settings/rbac')).data,
    staleTime: 60_000,
  })

  useEffect(() => {
    setRole(user.role)
    setModules(user.module_access)
    setError(null)
  }, [user])

  const grouped = useMemo(() => {
    const out = new Map<ModuleGroup, PlatformModuleId[]>()
    for (const id of WORKFLOW_ORDER) {
      const group = WORKFLOW_META[id]?.group as ModuleGroup | undefined
      if (!group) continue
      out.set(group, [...(out.get(group) ?? []), id])
    }
    return out
  }, [])

  const roleSpec = rbac.data?.roles[role]
  const chosen = useMemo(() => new Set(modules ?? []), [modules])
  const unrestricted = modules === null

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {}
      if (role !== user.role) body.role = role
      if (unrestricted) {
        if (user.module_access !== null) body.clear_module_access = true
      } else {
        body.module_access = modules
      }
      if (Object.keys(body).length === 0) return null
      return (await api.patch(`/admin/users/${user.id}`, body)).data
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'users'] })
      onClose()
    },
    onError: (e: unknown) => {
      const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail || 'The permissions could not be saved.')
    },
  })

  const toggleModule = (id: string) => {
    setModules((current) => {
      // First narrowing starts from what the person can see today, not from
      // an empty set — otherwise one click silently revokes everything else.
      const base = current ?? [...user.visible_modules]
      return base.includes(id) ? base.filter((m) => m !== id) : [...base, id]
    })
  }

  const toggleGroup = (ids: PlatformModuleId[]) => {
    setModules((current) => {
      const base = current ?? [...user.visible_modules]
      const all = ids.every((id) => base.includes(id))
      return all
        ? base.filter((m) => !ids.includes(m as PlatformModuleId))
        : [...new Set([...base, ...ids])]
    })
  }

  const emptySelection = !unrestricted && (modules?.length ?? 0) === 0

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Permissions for ${user.name}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col rounded-xl border border-amber-300/15 bg-[#0d0c0b] shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-amber-300/10 px-4 py-3">
          <div className="min-w-0">
            <div className="text-[9px] uppercase tracking-[0.16em] text-amber-200/70">Permissions</div>
            <div className="truncate text-[14px] font-medium text-stone-100">{user.name}</div>
            <div className="truncate text-[11px] text-stone-500">{user.email}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded p-1 text-stone-500 hover:text-stone-200"
          >
            <X size={14} />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-4 py-3 scrollbar-thin">
          {user.is_super_admin ? (
            <div className="flex items-center gap-2 rounded border border-amber-400/20 bg-amber-400/[0.06] px-3 py-2 text-[11px] text-amber-200">
              <ShieldCheck size={13} />
              A super admin reaches everything by definition. Role and module limits do not apply.
            </div>
          ) : null}

          {/* Role -------------------------------------------------------- */}
          <section>
            <div className="mb-1.5 flex items-baseline justify-between">
              <h3 className="text-[11px] font-semibold text-stone-200">Role — what they may do</h3>
              <button
                type="button"
                onClick={() => setShowRoleMatrix((v) => !v)}
                className="text-[10px] text-stone-500 hover:text-amber-200"
              >
                {showRoleMatrix ? 'Hide' : 'Show'} what this role grants
              </button>
            </div>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              disabled={rbac.isPending}
              className="w-full rounded border border-amber-300/15 bg-black/50 px-2 py-1.5 text-[12px] text-stone-200 outline-none focus:border-amber-400/40"
            >
              {Object.keys(rbac.data?.roles ?? { [role]: {} }).map((key) => (
                <option key={key} value={key}>
                  {rbac.data?.roleLabels?.[key] ?? key}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[10px] text-stone-600">
              The role is shared by everyone who holds it. Changing it here changes this person only;
              changing what the role itself grants affects everyone.
            </p>

            {showRoleMatrix ? (
              <div className="mt-2 max-h-56 overflow-auto rounded border border-white/[0.06] scrollbar-thin">
                <table className="w-full border-collapse text-[10px]">
                  <thead className="sticky top-0 bg-[#141312]">
                    <tr className="text-stone-500">
                      <th className="px-2 py-1 text-left font-normal">Module</th>
                      {ACTIONS.map((a) => (
                        <th key={a} className="px-2 py-1 font-normal capitalize">
                          {a}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {['*', ...WORKFLOW_ORDER].map((id) => {
                      const granted = roleActions(roleSpec, id)
                      return (
                        <tr key={id} className="border-t border-white/[0.04]">
                          <td className="px-2 py-1 text-left text-stone-400">{moduleLabel(id)}</td>
                          {ACTIONS.map((a) => (
                            <td key={a} className="px-2 py-1 text-center">
                              {granted.includes(a) ? (
                                <Check size={10} className="mx-auto text-amber-300" />
                              ) : (
                                <span className="text-stone-700">·</span>
                              )}
                            </td>
                          ))}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </section>

          {/* Module access ----------------------------------------------- */}
          <section>
            <h3 className="mb-1.5 text-[11px] font-semibold text-stone-200">
              Module access — where they may do it
            </h3>
            <div className="mb-2 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setModules(null)}
                aria-pressed={unrestricted}
                className={clsx(
                  'rounded border px-2 py-1 text-[10px]',
                  unrestricted
                    ? 'border-amber-400/40 bg-amber-400/[0.10] text-amber-200'
                    : 'border-white/[0.08] text-stone-400 hover:text-stone-200',
                )}
              >
                Unrestricted — follow the role
              </button>
              <button
                type="button"
                onClick={() => setModules(modules ?? [...user.visible_modules])}
                aria-pressed={!unrestricted}
                className={clsx(
                  'rounded border px-2 py-1 text-[10px]',
                  !unrestricted
                    ? 'border-amber-400/40 bg-amber-400/[0.10] text-amber-200'
                    : 'border-white/[0.08] text-stone-400 hover:text-stone-200',
                )}
              >
                Limit to specific modules
              </button>
            </div>

            <div className={clsx('grid gap-3 sm:grid-cols-2', unrestricted && 'pointer-events-none opacity-40')}>
              {[...grouped.entries()].map(([group, ids]) => (
                <div key={group} className="rounded border border-white/[0.06] p-2">
                  <button
                    type="button"
                    onClick={() => toggleGroup(ids)}
                    className="mb-1 text-[9px] uppercase tracking-[0.14em] text-stone-500 hover:text-amber-200"
                  >
                    {GROUP_LABELS[group]}
                  </button>
                  <div className="space-y-0.5">
                    {ids.map((id) => {
                      const on = unrestricted ? user.visible_modules.includes(id) : chosen.has(id)
                      const roleAllows = roleActions(roleSpec, id).includes('view')
                      return (
                        <button
                          key={id}
                          type="button"
                          role="checkbox"
                          aria-checked={on}
                          onClick={() => toggleModule(id)}
                          className="flex w-full items-center gap-1.5 rounded px-1 py-0.5 text-left text-[11px] hover:bg-white/[0.04]"
                        >
                          <Check size={10} className={on ? 'text-amber-300' : 'text-transparent'} />
                          <span className={on ? 'text-stone-200' : 'text-stone-500'}>{moduleLabel(id)}</span>
                          {on && !roleAllows && !user.is_super_admin ? (
                            <span
                              className="ml-auto text-[9px] text-stone-600"
                              title="Granted here, but this role has no view permission on it — so it stays hidden"
                            >
                              role denies
                            </span>
                          ) : null}
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>

            {emptySelection ? (
              <p className="mt-2 text-[10px] text-red-300">
                No modules selected. Pick at least one, or choose Unrestricted — saving an empty list
                would leave this person with nothing to open.
              </p>
            ) : null}
          </section>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-amber-300/10 px-4 py-3">
          <div className="min-w-0 text-[10px] text-red-300">{error}</div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded px-3 py-1 text-[11px] text-stone-400 hover:text-stone-200"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={save.isPending || emptySelection}
              onClick={() => save.mutate()}
              className="inline-flex items-center gap-1.5 rounded border border-amber-400/30 bg-amber-400/[0.12] px-3 py-1 text-[11px] text-amber-200 hover:bg-amber-400/[0.2] disabled:opacity-50"
            >
              {save.isPending ? <Loader2 size={11} className="motion-safe:animate-spin" /> : null}
              Save permissions
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
